import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMutation, useQuery } from '@apollo/client';
import { Card, CardContent } from '@guildeducationinc/recess';
import DeleteOutlineIcon from '@guildeducationinc/recess/icons/DeleteOutlineIcon';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav/AdminTopNav';
import { BENEFIT_PERIODS, DELETE_BENEFT_PERIOD, SAVE_BENEFT_PERIOD, } from '@src/graphql/queries/benefitPeriod';
import { useAuth } from '@src/hooks/useAuth';
import { useClientIdParam } from '@src/hooks/useClientIdParam';
import { BenefitPeriodPopulationEntityType, BenefitPeriodType } from '@src/types';
import { cleanCriteria, hasIncompleteConditions, hasValidConditions, } from '@src/utilities/criteriaHelper';
import { getPolicyNavLinks } from '@src/utilities/topNavHelper';
import { pick } from 'lodash';
import { Confirm, DataTable, EnumPicker, FilterGroupOperator, Form, RuleTargetType, TrackableButton, useAlert, useLocale, } from 'pe-core-frontend';
import { useEffect, useMemo, useState } from 'react';
import { RuleTargetFilter } from '../PolicyBuilder';
export const BenefitPeriods = () => {
    const clientId = useClientIdParam() || 0;
    const { isSuperuser } = useAuth();
    const { t, formatToLocalDate } = useLocale();
    const [type, setType] = useState(BenefitPeriodType.CAP_YEAR);
    const { showAlert } = useAlert();
    const { data, loading } = useQuery(BENEFIT_PERIODS, {
        variables: {
            spec: {
                type,
                clientId,
            },
        },
    });
    const [saveBenefitPeriod] = useMutation(SAVE_BENEFT_PERIOD, {
        refetchQueries: [BENEFIT_PERIODS],
    });
    const [deleteBenefitPeriod] = useMutation(DELETE_BENEFT_PERIOD, {
        refetchQueries: [BENEFIT_PERIODS],
    });
    const [selectedBenefitPeriod, setSelectedBenefitPeriod] = useState(null);
    const [benefitPeriodToDelete, setBenefitPeriodToDelete] = useState(null);
    const [saveError, setSaveError] = useState('');
    const [showValidationErrors, setShowValidationErrors] = useState(true);
    const [confirmDuplicateEveryone, setConfirmDuplicateEveryone] = useState(null);
    const benefitPeriods = useMemo(() => {
        const periods = data?.benefitPeriods || [];
        return [...periods].sort((a, b) => b.year - a.year);
    }, [data]);
    // Reset validation errors when opening/closing the form
    useEffect(() => {
        setShowValidationErrors(true);
        setSaveError('');
    }, [selectedBenefitPeriod]);
    const Fields = useMemo(() => [
        {
            name: 'sortOrder',
            label: t('Sort Order'),
            type: 'number',
        },
        {
            name: 'year',
            label: t('Year'),
            type: 'text',
        },
        type === BenefitPeriodType.CAP_YEAR || type === BenefitPeriodType.BUDGET_YEAR
            ? {
                name: 'segment',
                label: t('Segment'),
                type: 'text',
            }
            : undefined,
        {
            name: 'startDate',
            label: t('Start Date'),
            type: 'date',
        },
        {
            name: 'endDate',
            label: t('End Date'),
            type: 'date',
        },
    ].filter(x => x), [t]);
    const handleValidate = (benefitPeriod) => {
        // Enable showing validation errors when user attempts to save
        setShowValidationErrors(true);
        const errors = {};
        if (!benefitPeriod.startDate) {
            errors.startDate = t('Start Date is required');
        }
        if (!benefitPeriod.endDate) {
            errors.endDate = t('End Date is required');
        }
        if (benefitPeriod.startDate &&
            benefitPeriod.endDate &&
            benefitPeriod.startDate >= benefitPeriod.endDate) {
            errors.endDate = t('Benefit period end date must be after the start date');
        }
        // Validate that filter conditions are complete and exist
        if (benefitPeriod.target === RuleTargetType.FILTER) {
            if (hasIncompleteConditions(benefitPeriod.criteria)) {
                errors.criteria = 'All filter conditions must be completed before saving';
            }
            else if (!hasValidConditions(benefitPeriod.criteria)) {
                errors.criteria =
                    'At least one complete filter condition is required when using Specific Conditions';
            }
        }
        return errors;
    };
    const handleSave = async (benefitPeriod, skipDuplicateCheck = false) => {
        let result;
        try {
            setSaveError('');
            // Check for duplicate "everyone" rules for the same type and year, and confirm before proceeding
            if (!skipDuplicateCheck && benefitPeriod.target === RuleTargetType.ALL) {
                const existingEveryoneRule = benefitPeriods.find((bp) => bp.target === RuleTargetType.ALL &&
                    bp.type === benefitPeriod.type &&
                    bp.year === benefitPeriod.year &&
                    bp.id !== benefitPeriod.id);
                if (existingEveryoneRule) {
                    setConfirmDuplicateEveryone(benefitPeriod);
                    return;
                }
            }
            // When target is ALL (everyone), send null criteria to avoid validation errors
            // When target is FILTER, clean up any invalid conditions with empty op
            let criteria = null;
            if (benefitPeriod.target === RuleTargetType.FILTER) {
                criteria = cleanCriteria(benefitPeriod.criteria);
            }
            result = await saveBenefitPeriod({
                variables: {
                    input: {
                        ...pick(benefitPeriod, ['id', 'clientId', 'type', 'startDate', 'endDate', 'target']),
                        criteria,
                        year: Number(benefitPeriod.year),
                        sortOrder: Number(benefitPeriod.sortOrder) || 0,
                        segment: benefitPeriod.segment || null,
                    },
                },
            });
            if (result.data) {
                if (result.data.saveBenefitPeriod?.validationErrors) {
                    return result.data.saveBenefitPeriod?.validationErrors;
                }
            }
            showAlert(t('Benefit period saved'));
            setSelectedBenefitPeriod(null);
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const msg = e.message;
            setSaveError(t(msg));
        }
    };
    const handleDelete = async () => {
        if (benefitPeriodToDelete) {
            try {
                await deleteBenefitPeriod({
                    variables: {
                        id: benefitPeriodToDelete.id,
                    },
                });
                showAlert(t('Benefit period deleted'));
                setBenefitPeriodToDelete(null);
            }
            catch (e) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                showAlert(e.message, 'error');
            }
        }
    };
    const columns = useMemo(() => {
        const cols = [
            {
                field: 'sortOrder',
                headerName: t('Sort Order'),
                flex: 0.1,
                sortable: false,
                renderCell: ({ row }) => row.sortOrder,
            },
            {
                field: 'year',
                headerName: t('Year'),
                flex: 0.1,
                sortable: false,
                renderCell: ({ row }) => row.year,
            },
        ];
        if (type === BenefitPeriodType.CAP_YEAR || type === BenefitPeriodType.BUDGET_YEAR) {
            cols.push({
                field: 'segment',
                headerName: t('Segment'),
                flex: 0.1,
                sortable: false,
                renderCell: ({ row }) => row.segment,
            });
            cols.push({
                field: 'target',
                headerName: t('Target'),
                flex: 0.15,
                sortable: false,
                renderCell: ({ row }) => t(`policyBuilder.targetPopulation.${row.target}`),
            });
        }
        cols.push.apply(cols, [
            {
                field: 'startDate',
                headerName: t('Start Date'),
                flex: 0.2,
                sortable: false,
                renderCell: ({ row }) => {
                    if (row.startDate) {
                        return formatToLocalDate(row.startDate, { dateStyle: 'medium' });
                    }
                },
            },
            {
                field: 'endDate',
                headerName: t('End Date'),
                flex: 0.2,
                sortable: false,
                renderCell: ({ row }) => {
                    if (row.endDate) {
                        return formatToLocalDate(row.endDate, { dateStyle: 'medium' });
                    }
                },
            },
            {
                field: 'type',
                headerName: t('Type'),
                renderCell: ({ value }) => t(`benefitPeriodType.${value}`),
            },
            {
                field: 'action',
                headerName: t('Actions'),
                renderCell: ({ row }) => (isSuperuser && (_jsxs("div", { children: [_jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `benefit_period_${row.id}`, label: t('Edit'), appearance: 'minimal', icon: _jsx(EditIcon, { size: 20 }), onClick: () => setSelectedBenefitPeriod(row), className: 'margin--right_8' }), _jsx(TrackableButton, { btnType: 'icon', enText: 'Delete', tag: `benefit_period_${row.id}`, label: t('Delete'), appearance: 'minimal', icon: _jsx(DeleteOutlineIcon, { size: 20 }), onClick: () => setBenefitPeriodToDelete(row), className: 'margin--right_8' })] }))) ||
                    null,
                width: 100,
                sortable: false,
            },
        ]);
        return cols;
    }, [type, t]);
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('Policy'), navLinks: getPolicyNavLinks(t, clientId, 'benefit-periods'), actions: _jsxs(_Fragment, { children: [_jsx(EnumPicker, { isClearable: false, labelPrefix: 'benefitPeriodType', label: t('Type'), enumType: BenefitPeriodType, value: type, onChange: type => setType(type), useMui: true, btnStyle: { padding: '6px 16px' } }), isSuperuser && (_jsx(TrackableButton, { btnType: 'mui', enText: 'Add', tag: 'header', variant: 'contained', onClick: () => setSelectedBenefitPeriod({
                                id: 0,
                                clientId: clientId || 0,
                                type,
                                year: new Date().getFullYear(),
                                target: RuleTargetType.ALL,
                                criteria: {
                                    op: FilterGroupOperator.AND,
                                    conditions: [],
                                    groups: [],
                                },
                                sortOrder: 0,
                                startDate: '',
                                endDate: '',
                            }), children: t('Add') }))] }) }), _jsxs("div", { children: [!selectedBenefitPeriod && (_jsx(DataTable, { loading: loading, disableColumnMenu: true, disableRowSelectionOnClick: true, rows: benefitPeriods, pageSizeOptions: [], columns: columns })), selectedBenefitPeriod && (_jsx(Card, { children: _jsx(CardContent, { children: _jsx(Form, { title: selectedBenefitPeriod.id
                                    ? `${t('Edit')} ${t(`benefitPeriodType.${type}`)}`
                                    : `${t('Add')} ${t(`benefitPeriodType.${type}`)}`, modal: false, obj: selectedBenefitPeriod, fields: Fields, onValidate: handleValidate, onSave: handleSave, onCancel: () => {
                                    setSelectedBenefitPeriod(null);
                                    setShowValidationErrors(true);
                                }, error: saveError, children: ({ draft, setDraft, errors }) => (_jsx(_Fragment, { children: (type === BenefitPeriodType.CAP_YEAR ||
                                        type === BenefitPeriodType.BUDGET_YEAR) && (_jsxs("div", { children: [_jsx(RuleTargetFilter, { rule: draft, onChange: rule => {
                                                    // Clear validation errors when user starts editing
                                                    setShowValidationErrors(false);
                                                    setDraft({
                                                        ...draft,
                                                        target: rule.target,
                                                        criteria: rule.criteria,
                                                    });
                                                }, type: 'BenefitPeriod', clientId: clientId, targetPopulationEntityEnumType: BenefitPeriodPopulationEntityType, defaultTargetPopulationEntityType: BenefitPeriodPopulationEntityType.FILTER_EMPLOYEE }), showValidationErrors && (errors?.target || errors?.criteria) && (_jsxs("div", { className: 'text-red-600 mt-2 p-2 bg-red-50 border border-red-200 rounded', children: [errors?.target && _jsx("div", { children: errors.target }), errors?.criteria && _jsx("div", { children: errors.criteria })] }))] })) })) }) }) })), benefitPeriodToDelete && (_jsx(Confirm, { message: t('Are you sure you want to delete this benefit period?'), onConfirm: handleDelete, onCancel: () => setBenefitPeriodToDelete(null) })), confirmDuplicateEveryone && (_jsx(Confirm, { message: `A Benefit Period everyone rule already exists for year ${confirmDuplicateEveryone.year}. Are you sure you want to create another?`, onConfirm: () => {
                            handleSave(confirmDuplicateEveryone, true);
                            setConfirmDuplicateEveryone(null);
                        }, onCancel: () => setConfirmDuplicateEveryone(null) }))] })] }));
};
